/**
 * /app/{tenantSlug}/settings/entities — Settings → Organization → Entities
 *
 * Full CRUD page for legal entities (G18). Surfaces:
 *  - List with primary badge + active/archived status
 *  - Create dialog: name, code, country, tax_id, currency, FY start month
 *  - Edit dialog: all mutable fields (name, country, tax_id, currency,
 *    fy_start_month)
 *  - Set Primary button (HR-admin) — swaps the primary flag
 *  - Archive button (HR-admin) — soft-archives non-primary entities
 *
 * G18 Phase 2 (the mass per-query WHERE entity_id backend rewrite) is
 * a separate batch — this page ships the Vite UI surface only.
 * Existing single-entity tenants work because the G18 backfill ensures
 * every row has entity_id = primary.
 *
 * Migration note (2026-06-06, Next.js → Vite Phase 2): new page, no
 * equivalent in the Next.js frontend (entities admin never got a
 * dedicated settings leaf there — only the switcher component existed).
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Archive,
  Building2,
  Loader2,
  PlusCircle,
  Star,
} from "lucide-react";
import { toast } from "sonner";
import { toApiError } from "@/lib/api";
import {
  useEntities,
  useCreateEntity,
  useUpdateEntity,
  useSetEntityPrimary,
  useArchiveEntity,
} from "@/hooks/use-entities";
import type { LegalEntity } from "@/lib/api/entities";
import { useHrRoles } from "@/components/hr/use-hr-roles";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { BankAccountsCard } from "@/components/payroll/bank-accounts-card";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/entities",
)({
  component: SettingsEntitiesRoute,
});

// ---------------------------------------------------------------------------
// Form schemas
// ---------------------------------------------------------------------------

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const createSchema = z.object({
  name: z.string().min(1, "Name is required").max(120, "Max 120 characters"),
  code: z
    .string()
    .min(2, "Code must be at least 2 characters")
    .max(20, "Max 20 characters")
    .regex(/^[A-Z0-9_-]+$/, "Use only uppercase letters, digits, _ or -"),
  country_code: z
    .string()
    .length(2, "Must be a 2-letter ISO country code")
    .regex(/^[A-Z]{2}$/, "Must be uppercase ISO-3166 alpha-2"),
  tax_id: z.string().max(40, "Max 40 characters").optional(),
  currency_code: z
    .string()
    .length(3, "Must be a 3-letter ISO currency code")
    .regex(/^[A-Z]{3}$/, "Must be uppercase ISO-4217"),
  fy_start_month: z.coerce
    .number()
    .int()
    .min(1)
    .max(12),
});

const editSchema = z.object({
  name: z.string().min(1, "Name is required").max(120, "Max 120 characters"),
  country_code: z
    .string()
    .length(2, "Must be a 2-letter ISO country code")
    .regex(/^[A-Z]{2}$/, "Must be uppercase ISO-3166 alpha-2"),
  tax_id: z.string().max(40, "Max 40 characters").optional(),
  currency_code: z
    .string()
    .length(3, "Must be a 3-letter ISO currency code")
    .regex(/^[A-Z]{3}$/, "Must be uppercase ISO-4217"),
  fy_start_month: z.coerce
    .number()
    .int()
    .min(1)
    .max(12),
});

type CreateValues = z.infer<typeof createSchema>;
type EditValues = z.infer<typeof editSchema>;

// ---------------------------------------------------------------------------
// CreateEntityDialog
// ---------------------------------------------------------------------------

function CreateEntityDialog() {
  const [open, setOpen] = useState(false);
  const create = useCreateEntity();

  const form = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      name: "",
      code: "",
      country_code: "IN",
      tax_id: "",
      currency_code: "INR",
      fy_start_month: 4,
    },
  });

  const handleClose = (v: boolean) => {
    setOpen(v);
    if (!v) form.reset();
  };

  const onSubmit = async (values: CreateValues) => {
    try {
      const entity = await create.mutateAsync({
        name: values.name.trim(),
        code: values.code.trim().toUpperCase(),
        country_code: values.country_code.trim().toUpperCase(),
        tax_id: values.tax_id?.trim() || undefined,
        currency_code: values.currency_code.trim().toUpperCase(),
        fy_start_month: values.fy_start_month,
      });
      toast.success(`${entity.name} created.`);
      handleClose(false);
    } catch (err) {
      const e = toApiError(err);
      if (e.field === "code") {
        form.setError("code", { message: e.message });
      } else if (e.field === "name") {
        form.setError("name", { message: e.message });
      } else {
        toast.error(e.message || "Couldn't create entity.");
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogTrigger asChild>
        <Button size="sm" className="gap-1.5">
          <PlusCircle className="h-4 w-4" />
          Add entity
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create legal entity</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Acme India Pvt. Ltd." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Code *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g. ACME-IN"
                      {...field}
                      onChange={(e) =>
                        field.onChange(e.target.value.toUpperCase())
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="country_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Country code *</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="IN"
                        maxLength={2}
                        {...field}
                        onChange={(e) =>
                          field.onChange(e.target.value.toUpperCase())
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="currency_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Currency *</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="INR"
                        maxLength={3}
                        {...field}
                        onChange={(e) =>
                          field.onChange(e.target.value.toUpperCase())
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="tax_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tax ID (GSTIN / PAN)</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. 22AAAAA0000A1Z5" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="fy_start_month"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>FY start month *</FormLabel>
                  <Select
                    value={String(field.value)}
                    onValueChange={(v) => field.onChange(Number(v))}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {MONTHS.map((m) => (
                        <SelectItem key={m.value} value={String(m.value)}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleClose(false)}
                disabled={create.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? (
                  <>
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    Creating…
                  </>
                ) : (
                  "Create entity"
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// EditEntityDialog
// ---------------------------------------------------------------------------

interface EditEntityDialogProps {
  entity: LegalEntity;
}

function EditEntityDialog({ entity }: EditEntityDialogProps) {
  const [open, setOpen] = useState(false);
  const update = useUpdateEntity();

  const form = useForm<EditValues>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      name: entity.name,
      country_code: entity.country_code,
      tax_id: entity.tax_id ?? "",
      currency_code: entity.currency_code,
      fy_start_month: entity.fy_start_month,
    },
  });

  // Sync defaults when the entity prop updates (e.g. after a list refetch).
  const handleOpen = (v: boolean) => {
    setOpen(v);
    if (v) {
      form.reset({
        name: entity.name,
        country_code: entity.country_code,
        tax_id: entity.tax_id ?? "",
        currency_code: entity.currency_code,
        fy_start_month: entity.fy_start_month,
      });
    }
  };

  const onSubmit = async (values: EditValues) => {
    try {
      await update.mutateAsync({
        id: entity.id,
        body: {
          name: values.name.trim(),
          country_code: values.country_code.trim().toUpperCase(),
          tax_id: values.tax_id?.trim() || undefined,
          currency_code: values.currency_code.trim().toUpperCase(),
          fy_start_month: values.fy_start_month,
        },
      });
      toast.success(`${values.name} updated.`);
      setOpen(false);
    } catch (err) {
      const e = toApiError(err);
      if (e.field === "name") {
        form.setError("name", { message: e.message });
      } else {
        toast.error(e.message || "Couldn't update entity.");
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit {entity.name}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name *</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="country_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Country code *</FormLabel>
                    <FormControl>
                      <Input
                        maxLength={2}
                        {...field}
                        onChange={(e) =>
                          field.onChange(e.target.value.toUpperCase())
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="currency_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Currency *</FormLabel>
                    <FormControl>
                      <Input
                        maxLength={3}
                        {...field}
                        onChange={(e) =>
                          field.onChange(e.target.value.toUpperCase())
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="tax_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tax ID (GSTIN / PAN)</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="fy_start_month"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>FY start month *</FormLabel>
                  <Select
                    value={String(field.value)}
                    onValueChange={(v) => field.onChange(Number(v))}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {MONTHS.map((m) => (
                        <SelectItem key={m.value} value={String(m.value)}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={update.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? (
                  <>
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Save changes"
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// SetPrimaryButton
// ---------------------------------------------------------------------------

interface SetPrimaryButtonProps {
  entity: LegalEntity;
}

function SetPrimaryButton({ entity }: SetPrimaryButtonProps) {
  const setPrimary = useSetEntityPrimary();

  const onClick = async () => {
    try {
      await setPrimary.mutateAsync(entity.id);
      toast.success(`${entity.name} is now the primary entity.`);
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onClick}
      disabled={setPrimary.isPending}
      title="Set as primary entity"
    >
      {setPrimary.isPending ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Star className="h-3.5 w-3.5" />
      )}
      Set primary
    </Button>
  );
}

// ---------------------------------------------------------------------------
// ArchiveEntityButton
// ---------------------------------------------------------------------------

interface ArchiveEntityButtonProps {
  entity: LegalEntity;
}

function ArchiveEntityButton({ entity }: ArchiveEntityButtonProps) {
  const archive = useArchiveEntity();

  const onConfirm = async () => {
    try {
      await archive.mutateAsync(entity.id);
      toast.success(`${entity.name} archived.`);
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="text-destructive hover:text-destructive"
          disabled={archive.isPending}
        >
          {archive.isPending ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Archive className="mr-1.5 h-3.5 w-3.5" />
          )}
          Archive
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Archive {entity.name}?</AlertDialogTitle>
          <AlertDialogDescription>
            The entity will be deactivated and hidden from the switcher.
            Existing records linked to it are preserved. You cannot archive the
            primary entity.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Archive
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ---------------------------------------------------------------------------
// EntityCard
// ---------------------------------------------------------------------------

interface EntityCardProps {
  entity: LegalEntity;
  isHrAdmin: boolean;
}

function EntityCard({ entity, isHrAdmin }: EntityCardProps) {
  const fyMonth = MONTHS.find((m) => m.value === entity.fy_start_month)?.label;

  return (
    <Card className={entity.is_active ? undefined : "opacity-60"}>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
              <CardTitle className="text-base">{entity.name}</CardTitle>
              {entity.is_primary && (
                <Badge variant="default" className="text-xs">
                  Primary
                </Badge>
              )}
              {!entity.is_active && (
                <Badge variant="secondary" className="text-xs">
                  Archived
                </Badge>
              )}
            </div>
            <p className="mt-1 text-xs text-muted-foreground font-mono">
              {entity.code} · {entity.country_code} · {entity.currency_code}
              {entity.tax_id && ` · ${entity.tax_id}`}
              {fyMonth && ` · FY starts ${fyMonth}`}
            </p>
          </div>

          {isHrAdmin && entity.is_active && (
            <div className="flex flex-wrap items-center gap-2">
              <EditEntityDialog entity={entity} />
              {!entity.is_primary && (
                <>
                  <SetPrimaryButton entity={entity} />
                  <ArchiveEntityButton entity={entity} />
                </>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      {entity.is_active && (
        <CardContent className="pt-0">
          <BankAccountsCard entityId={entity.id} canEdit={isHrAdmin} />
        </CardContent>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Route component
// ---------------------------------------------------------------------------

function SettingsEntitiesRoute() {
  const { isHrAdmin } = useHrRoles();
  const q = useEntities();
  const entities = q.data?.items ?? [];

  const active = entities.filter((e) => e.is_active);
  const archived = entities.filter((e) => !e.is_active);

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">Legal entities</h2>
              <p className="text-sm text-muted-foreground">
                Manage the legal entities within your organisation. The primary
                entity is used when no specific entity is selected.
              </p>
            </div>
            {isHrAdmin && <CreateEntityDialog />}
          </div>

          {!isHrAdmin && (
            <Alert>
              <AlertTitle>Read-only</AlertTitle>
              <AlertDescription>
                Only HR managers and company admins can manage legal entities.
              </AlertDescription>
            </Alert>
          )}

          {q.isLoading && (
            <div className="space-y-3">
              {["s1", "s2"].map((k) => (
                <Skeleton key={k} className="h-20 w-full" />
              ))}
            </div>
          )}

          {q.isError && (
            <Alert variant="destructive">
              <AlertTitle>Couldn&apos;t load entities</AlertTitle>
              <AlertDescription>
                {toApiError(q.error).message}
              </AlertDescription>
            </Alert>
          )}

          {q.data && active.length === 0 && archived.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-sm text-muted-foreground">
                No legal entities yet.
                {isHrAdmin && " Use Add entity to create one."}
              </CardContent>
            </Card>
          )}

          {active.length > 0 && (
            <div className="space-y-3">
              {active.map((e) => (
                <EntityCard key={e.id} entity={e} isHrAdmin={isHrAdmin} />
              ))}
            </div>
          )}

          {archived.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground">
                Archived
              </h3>
              {archived.map((e) => (
                <EntityCard key={e.id} entity={e} isHrAdmin={isHrAdmin} />
              ))}
            </div>
          )}
        </div>
      </SettingsShell>
    </>
  );
}
